<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" title="Logs">
      <button class="btn btn-secondary" v-submitting="loading" @click="getLogs()">
        Refresh <fa :icon="['far', 'redo-alt']" class="ml-2" />
      </button>
    </PageHeader>
    
    <div class="container">
      <!-- List -->
      <div class="card">
        <div class="flex flex-col justify-between p-4 border-b md:flex-row lg:p-5">
          <div class="flex flex-col space-y-3 md:space-y-0 md:flex-row md:space-x-3">
            <filter-dropdown @apply="applyFilter" @clear="clearFilter" :loading="loading">
              <div>
                <label for="created_at" class="form-label">Date Range</label>
                <DatePicker color="indigo" mode="dateTime" is-range is24hr :model-config="{ start: { timeAdjust: '00:00:00' }, end: { timeAdjust: '23:59:59' } }" v-model="filter.created_at">
                  <template v-slot="{ inputValue, inputEvents }">
                    <div class="flex justify-center">
                      <input
                        :value="inputValue.start"
                        v-on="inputEvents.start"
                        class="form-input prepend"
                      />
                      <div class="flex items-center justify-center flex-none w-10 text-gray-500 border-t border-b border-gray-300">
                        &rarr;
                      </div>
                      <input
                        :value="inputValue.end"
                        v-on="inputEvents.end"
                        class="form-input append"
                      />
                    </div>
                  </template>
                </DatePicker>
              </div>
              <div>
                <label for="type" class="form-label">Type</label>
                <automation-type-select type="type" v-model="filter.type" />
              </div>
              <div>
                <label for="driver" class="form-label">Driver</label>
                <driver-select v-model="filter.driver" />
              </div>
              <div>
                <label for="vehicle" class="form-label">Vehicle</label>
                <vehicle-select v-model="filter.vehicle" />
              </div>
              <div>
                <label for="acknowledgement_received" class="flex items-center form-label">
                  <input type="checkbox" id="acknowledgement_received" class="mr-2 form-checkbox" v-model="filter.acknowledgement_received" /> Acknowledged
                </label>
              </div>
            </filter-dropdown>
            <sort-dropdown :options="sortOptions" />
            <button class="btn btn-sm btn-secondary" v-submitting="submitting" @click="exportLogs" v-if="user.type === 'admin'">
              Export
            </button>
          </div>
        </div>

        <!-- Loading -->
        <Loading v-if="loading" class="p-6" />

        <!-- Empty -->
        <div v-else-if="logs.meta.total < 1 && !$route.query.filter" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No automations have been logged yet.
          </div>
          <div class="text-gray-500">
            Any time an automation is triggered it will be logged here.
          </div>
          <button class="mt-4 btn btn-outline" @click="getLogs()">
            Refresh
          </button>
        </div>

        <!-- No Results -->
        <div v-else-if="logs.meta.total < 1 && $route.query.filter" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No logs matching your filters.
          </div>
          <div class="text-gray-500">
            Try changing your filter settings, or clear the filter to see latest logs.
          </div>
          <button class="mt-4 btn btn-outline" @click="clearFilter">
            Clear Filters
          </button>
        </div>

        <!-- Results -->
        <div v-else>
          <!-- Table -->
          <div class="flex flex-col overflow-hidden">
            <div class="py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
              <div class="inline-block min-w-full overflow-hidden align-middle rounded-t-lg">
                <table class="min-w-full">
                  <thead>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Time
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Type
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Vehicle
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Driver
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Ack/Note
                    </th>
                  </thead>
                  <tbody>
                    <tr v-for="log in logs.data" :key="log.id" class="cursor-pointer hover:bg-gray-50" @click="viewLog(log)">
                      <td class="px-6 py-2 text-gray-500 border-b whitespace-nowrap" >
                        {{ log.created_at | moment('DD MMM HH:mm:ss') }}
                      </td>
                      <td class="px-6 py-2 border-b whitespace-nowrap" >
                        <span class="inline-flex text-xs font-bold tracking-wider uppercase bg-gray-100 rounded-lg">
                          <span class="flex items-center px-2 text-gray-500 bg-gray-200 rounded-l-lg">
                            <fa :icon="['far', automationTypes[log.type].icon]" />
                          </span>
                          <span class="px-3 py-1">
                            {{ automationTypes[log.type].name }}
                          </span>
                        </span>
                      </td>
                      <td class="px-6 py-2 border-b whitespace-nowrap">
                        <template v-if="log.vehicle">
                          {{ log.vehicle.objectname }} 
                          <span class="px-2 text-xs text-gray-500 bg-gray-100 rounded-full">
                            {{ log.vehicle.objectno }}
                          </span>
                        </template>
                      </td>
                      <td class="px-6 py-2 border-b whitespace-nowrap" >
                        <template v-if="log.driver">
                          {{ log.driver.name }}
                          <span class="px-2 text-xs text-gray-500 bg-gray-100 rounded-full">
                            {{ log.driver.driverno }}
                          </span>
                        </template>
                      </td>
                      <td class="px-6 py-2 space-x-3 border-b whitespace-nowrap">
                        <fa :icon="['far', 'check']" class="opacity-0 text-emerald-400" 
                          :class="{ 'opacity-100': log.acknowledgement_received }"
                          v-tooltip="`Alert was acknowledged`" />
                        <fa :icon="['far', 'sticky-note']" class="text-gray-500 opacity-0" 
                          :class="{ 'opacity-100': log.acknowledgement_note }"
                          v-tooltip="`Log has a note`" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <Pagination class="rounded-b-lg" :total="logs.meta.total" :per-page="logs.meta.per_page" :current-page="logs.meta.current_page" />
        </div>
      </div>
    </div>

    <Modal :open.sync="showLogModal" title="View Log" size="sm:max-w-2xl">
      <div class="p-6" v-if="log">
        <div class="space-y-4">
          <div class="grid grid-cols-2">
            <div>
              <label class="text-xs font-medium tracking-wider text-gray-500 uppercase">Date</label>
              <div>{{ log.created_at | moment('DD MMM YYYY') }}</div>
            </div>

            <div>
              <label class="text-xs font-medium tracking-wider text-gray-500 uppercase">Time</label>
              <div>{{ log.created_at | moment('HH:mm:ss') }}</div>
            </div>
          </div>

          <div>
            <label class="text-xs font-medium tracking-wider text-gray-500 uppercase">Type</label>
            <div>{{ automationTypes[log.type].name }}</div>
          </div>

          <div class="grid grid-cols-2">
            <div>
              <label class="text-xs font-medium tracking-wider text-gray-500 uppercase">Vehicle</label>
              <div>
                <template v-if="log.vehicle">
                  {{ log.vehicle.objectname }} 
                  <span class="px-2 text-xs text-gray-500 bg-gray-100 rounded-full">
                    {{ log.vehicle.objectno }}
                  </span>
                </template>
              </div>
            </div>

            <div>
              <label class="text-xs font-medium tracking-wider text-gray-500 uppercase">Driver</label>
              <div>
                <template v-if="log.driver">
                  {{ log.driver.name }}
                  <span class="px-2 text-xs text-gray-500 bg-gray-100 rounded-full">
                    {{ log.driver.driverno }}
                  </span>
                </template>
              </div>
            </div>
          </div>

          <div>
            <label class="text-xs font-medium tracking-wider text-gray-500 uppercase">Message Sent</label>
            <div>"{{ log.message }}"</div>
          </div>

          <div class="grid grid-cols-2">
            <div class="flex items-center">
              <fa :icon="['far', log.trigger_dashboard_alert ? 'check-square' : 'square' ]" fixed-width class="mr-1" />
              <label class="flex items-center text-xs font-medium tracking-wider text-gray-500 uppercase">
                Triggered Monitor Alert? 
              </label>
            </div>

            <div class="flex items-center">
              <fa :icon="['far', log.acknowledgement_received ? 'check-square' : 'square' ]" fixed-width class="mr-1" />
              <label class="flex items-center text-xs font-medium tracking-wider text-gray-500 uppercase">
                Acknowledged?
              </label>
            </div>
          </div>

          <form @submit.prevent="updateLog">
            <fieldset class="form-group">
              <label for="acknowledgement_note" class="text-xs font-medium tracking-wider text-gray-500 uppercase">Note</label>
              <textarea name="acknowledgement_note" id="acknowledgement_note" cols="30" rows="3" class="form-textarea" v-model="log.acknowledgement_note"></textarea>
            </fieldset>

            <button type="submit" class="btn btn-primary" v-submitting="submitting">Save Changes</button>
          </form>

          <div class="px-3 py-2 border rounded bg-gray-50">
            <label class="flex items-center justify-between mb-0 text-xs font-medium tracking-wider text-gray-500 uppercase" @click="log.showRawOptions = !log.showRawOptions">
              Raw Options Data <fa :icon="['far', 'chevron-down']" class="transition duration-150 transform" :class="{ 'rotate-180': log.showRawOptions }" />
            </label>
            <Collapse :active="log.showRawOptions">
              <div class="mt-2 text-xs text-gray-500">{{ log.options }}</div>
            </Collapse>
          </div>

          <div class="px-3 py-2 border rounded bg-gray-50">
            <label class="flex items-center justify-between mb-0 text-xs font-medium tracking-wider text-gray-500 uppercase" @click="log.showRawEvent = !log.showRawEvent">
              Raw Event Data <fa :icon="['far', 'chevron-down']" class="transition duration-150 transform" :class="{ 'rotate-180': log.showRawEvent }" />
            </label>
            <Collapse :active="log.showRawEvent">
              <div class="mt-2 text-xs text-gray-500">{{ log.event }}</div>
            </Collapse>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { sub } from 'date-fns'
import AutomationTypeSelect from '@/components/AutomationTypeSelect'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
import FilterDropdown from '@/components/FilterDropdown'
import SortDropdown from '@/components/SortDropdown'
import DriverSelect from '@/components/DriverSelect'
import VehicleSelect from '@/components/VehicleSelect'

export default {
  name: 'LogList',

  metaInfo: {
    title: 'Logs'
  },

  components: {
    AutomationTypeSelect,
    DatePicker,
    FilterDropdown,
    SortDropdown,
    DriverSelect,
    VehicleSelect
  },

  created () {
    this.setFilter()
    this.getLogs()
  },

  computed: {
    ...mapState(['automationTypes', 'user'])
  },

  data () {
    return {
      loading: true,
      submitting: false,
      logs: {},
      log: null,
      showLogModal: false,
      filter: {
        created_at: { start: sub(new Date(), { months: 1 }), end: new Date() },
        type: null,
        vehicle: null,
        driver: null,
        acknowledgement_received: null,
        acknowledgement_note: null
      },
      sortOptions: [
        { label: `Time <span class="text-gray-500">(Oldest First)</span>`, prop: 'created_at', dir: 'asc' },
        { label: `Time <span class="text-gray-500">(Newest First)</span>`, prop: 'created_at', dir: 'desc' },
        { label: `Type <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'type', dir: 'asc' },
        { label: `Type <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'type', dir: 'desc' },
        { label: `Driver <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'driver_id', dir: 'asc' },
        { label: `Driver <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'driver_id', dir: 'desc' },
        { label: `Vehicle <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'vehicle_id', dir: 'asc' },
        { label: `Vehicle <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'vehicle_id', dir: 'desc' }
      ]
    }
  },

  methods: {
    async getLogs () {
      try {
        this.loading = true
        const { data } = await this.$api.get(`account/${this.$route.params.account}/automation-logs`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'created_at',
            dir: this.$route.query.dir || 'desc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.logs = data.logs
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    setFilter () {
      if (this.$route.query.filter) {
        this.filter = JSON.parse(this.$route.query.filter)
      }
    },

    applyFilter () {
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: JSON.stringify(this.filter)
        } 
      })
    },

    clearFilter () {
      this.filter = {
        created_at: { start: sub(new Date(), { months: 1 }), end: new Date() },
        type: null,
        vehicle: null,
        driver: null,
        acknowledgement_received: null,
        acknowledgement_note: null
      }
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: undefined
        } 
      })
    },

    viewLog (log) {
      this.log = {
        ...log,
        showRawOptions: false,
        showRawEvent: false
      }
      this.showLogModal = true
    },

    async updateLog () {
      try {
        this.submitting = true
        let url = `account/${this.$route.params.account}/automation-logs/${this.log.id}`
        const { data } = await this.$api.patch(url, this.log)
        this.getLogs()
        this.showLogModal = false
        this.$toasted.show('Log note updated.')
      } catch (error) {
        console.error(error)
      } finally {
        this.submitting = false
      }
    },

    async exportLogs () {
      try {
        this.submitting = true
        const { data } = await this.$api.get(`account/${this.$route.params.account}/automation-logs/export`, {
          params: {
            sort: this.$route.query.sort || 'created_at',
            dir: this.$route.query.dir || 'desc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.$toasted.show(`Logs exported.`)
        window.open(data.filePath, '_blank')
      } catch (error) {
        console.error(error)
      } finally {
        this.submitting = false
      }
    },
  },

  watch: {
    '$route.query': {
      handler (newVal, oldVal) {
        this.getLogs()
      },
      deep: true
    }
  }
}
</script>